import SurveyItem from './SurveyItem';

export type DependencyCheckStatus =
  | 'not-affected'
  | 'needs-update'
  | 'unresolvable';
export type DependencyTypes = 'logic' | 'intial-value';

export interface SingleDependencyCheckResult {
  readonly checkStatus: DependencyCheckStatus;
  readonly dependencyType: DependencyTypes;
  readonly updatedSurveyItem?: SurveyItem;
}

export interface DependencyCheckResult {
  readonly checkStatus: DependencyCheckStatus;
  readonly logicResults: SingleDependencyCheckResult[];
}

/**
 * Interface for dependency check types to comply to
 *
 * @interface IDependencyType
 */
interface Dependency {
  linkId: string;
  hasDependency: (surveyItem: SurveyItem) => boolean;
  isValid: boolean;
  onRemoval: (surveyItem: SurveyItem) => SingleDependencyCheckResult;
  onUpdate: (
    originalSurveyItem: SurveyItem,
    updatedSurveyItem: SurveyItem,
  ) => SingleDependencyCheckResult;
}

/**
 * A dependency on a survey item due to the survey item's logic rules
 */
export class LogicDependency implements Dependency {
  public readonly linkId: string;
  private _dependentSurveyItem: SurveyItem;
  private readonly _notAffectedCheckResult: SingleDependencyCheckResult = {
    checkStatus: 'not-affected',
    dependencyType: 'logic',
  };

  constructor(dependentSurveyItem: SurveyItem) {
    if (!dependentSurveyItem) {
      console.error(
        'Attempted to initialize a logic dependency with a null surveyItem',
      );
    } else {
      this._dependentSurveyItem = dependentSurveyItem;
      this.linkId = dependentSurveyItem.link;
    }
  }

  _getResultWithoutDependentLogicRules(
    filterQuestionLinkId: string,
  ): SingleDependencyCheckResult {
    if (!this._dependentSurveyItem || !filterQuestionLinkId) {
      return this._notAffectedCheckResult;
    }

    // when removing a survey item that this item is logically dependent on, just remove the rules
    const rulesWithoutDependency =
      this._dependentSurveyItem.itemEnablement.copyFhirEnableWhenWithoutLinkRules(
        filterQuestionLinkId,
      );
    const updatedQuestionnaireItem: fhir.QuestionnaireItem = {
      ...this._dependentSurveyItem.fhirQuestionnaireItem,
      enableWhen: rulesWithoutDependency,
    };

    return {
      checkStatus: 'needs-update',
      dependencyType: 'logic',
      updatedSurveyItem: new SurveyItem(
        updatedQuestionnaireItem,
        this._dependentSurveyItem.isRootItem,
      ),
    };
  }

  /**
   * if a choice set changes (ex: to [1,2,3]) and a previous rule contains a check for a non-existent
   * set value (ex: 4), remove that rule as it will never apply
   */
  _getResultWithoutNonexistentRuleValues(
    updatedSurveyItem: SurveyItem,
  ): SingleDependencyCheckResult {
    if (!updatedSurveyItem.isChoiceBasedType) {
      return this._notAffectedCheckResult;
    }

    if (updatedSurveyItem.optionList.length === 0) {
      // all options removed
      return this._getResultWithoutDependentLogicRules(updatedSurveyItem.link);
    }

    const configuredValues = updatedSurveyItem.optionList.map(
      option => option.value,
    );
    const currentEnablment = this._dependentSurveyItem.itemEnablement;
    const targetLinkId = updatedSurveyItem.link;
    const rulesWithValidValues =
      currentEnablment.copyFhirEnableWhenWithoutNonExistentValues(
        targetLinkId,
        configuredValues,
      );

    if (
      rulesWithValidValues.length ===
      currentEnablment.originalEnablementRules.length
    ) {
      // no rules removed
      return this._notAffectedCheckResult;
    }

    const updatedQuestionnaireItem: fhir.QuestionnaireItem = {
      ...this._dependentSurveyItem.fhirQuestionnaireItem,
      enableWhen: rulesWithValidValues,
    };

    return {
      checkStatus: 'needs-update',
      dependencyType: 'logic',
      updatedSurveyItem: new SurveyItem(
        updatedQuestionnaireItem,
        this._dependentSurveyItem.isRootItem,
      ),
    };
  }

  get isValid() {
    return (
      !!this._dependentSurveyItem &&
      !!this._dependentSurveyItem.itemEnablement &&
      !!this._dependentSurveyItem.itemEnablement.hasEnablementCriteria
    );
  }

  hasDependency(surveyItem: SurveyItem): boolean {
    if (!this.isValid) return false;

    return this._dependentSurveyItem.itemEnablement.dependentItemLinks.includes(
      surveyItem.link,
    );
  }

  onRemoval(surveyItem: SurveyItem): SingleDependencyCheckResult {
    if (!this.hasDependency(surveyItem)) return this._notAffectedCheckResult;

    // when removing a survey item that this item is logically dependent on, just remove the rules
    return this._getResultWithoutDependentLogicRules(surveyItem.link);
  }

  onUpdate(
    originalSurveyItem: SurveyItem,
    updatedSurveyItem: SurveyItem,
  ): SingleDependencyCheckResult {
    if (!this.hasDependency(originalSurveyItem)) {
      return this._notAffectedCheckResult;
    }

    if (
      originalSurveyItem.isChoiceBasedType &&
      updatedSurveyItem.isChoiceBasedType
    ) {
      // both previous and new are choice based.  it may have changed from open-choice <-> choice, but that can be valid
      if (
        originalSurveyItem.choiceAnswerType !==
        updatedSurveyItem.choiceAnswerType
      ) {
        // both previous and current are choice based, but the type of choices has changed.  that is a breaking change
        return this._getResultWithoutDependentLogicRules(
          originalSurveyItem.link,
        );
      } else if (
        originalSurveyItem.optionReference !== updatedSurveyItem.optionReference
      ) {
        // the referenced option list change.  this is a breaking change
        return this._getResultWithoutDependentLogicRules(
          originalSurveyItem.link,
        );
      } else if (
        originalSurveyItem.hasOptionReference &&
        updatedSurveyItem.hasOptionReference &&
        originalSurveyItem.optionReference === updatedSurveyItem.optionReference
      ) {
        // these choices reference the same option list, values must be equal
        return this._notAffectedCheckResult;
      }

      /*
        we want to intelligently enforce dependency changes. If logic references a set, but the user
        only added to a set, this is not breaking.  If a user removed a reference value, this is breaking.
      */
      return this._getResultWithoutNonexistentRuleValues(updatedSurveyItem);
    } else if (originalSurveyItem.type !== updatedSurveyItem.type) {
      // changing type (that is not a choice type) is always a change that requires removal of rules
      return this._getResultWithoutDependentLogicRules(originalSurveyItem.link);
    } else {
      // this is not a breaking change
      return this._notAffectedCheckResult;
    }
  }
}

const notAffectedCheckResult: DependencyCheckResult = {
  checkStatus: 'not-affected',
  logicResults: [],
};

class ResultsAggregation {
  _getStatusFilter(
    status: DependencyCheckStatus,
  ): (singleResult: SingleDependencyCheckResult) => boolean {
    return singleResult => singleResult && singleResult.checkStatus === status;
  }

  _getTypeFilter(
    type: DependencyTypes,
  ): (singleResult: SingleDependencyCheckResult) => boolean {
    return singleResult => singleResult && singleResult.dependencyType === type;
  }

  getAggregateResult(
    singleResults: SingleDependencyCheckResult[] = [],
  ): DependencyCheckResult {
    if (!singleResults || !singleResults.length) return notAffectedCheckResult;

    const unresolvableResults = singleResults.filter(
      this._getStatusFilter('unresolvable'),
    );
    const needsUpdate = singleResults.filter(
      this._getStatusFilter('needs-update'),
    );

    if (unresolvableResults.length) {
      const logicResults = unresolvableResults.filter(
        this._getTypeFilter('logic'),
      );
      return {
        checkStatus: 'unresolvable',
        logicResults,
      };
    } else if (needsUpdate.length) {
      const logicResults = needsUpdate.filter(this._getTypeFilter('logic'));
      return {
        checkStatus: 'needs-update',
        logicResults,
      };
    } else {
      // there were only not-affected results
      return notAffectedCheckResult;
    }
  }
}

export default class SurveyDependencies {
  static notAffectedResult: DependencyCheckResult = notAffectedCheckResult;
  private _dependencyCollection: Dependency[];
  private _resultAggregator = new ResultsAggregation();

  static combineDependencyResults(results: DependencyCheckResult[] = []) {
    const combinedIndividualResults: SingleDependencyCheckResult[] = [];
    results.forEach(result => {
      combinedIndividualResults.push(...(result.logicResults || []));
    });

    const aggregator = new ResultsAggregation();
    return aggregator.getAggregateResult(combinedIndividualResults);
  }

  constructor(surveyItems: SurveyItem[] = []) {
    const dependencies: Dependency[] = [];

    // load up dependency collection
    surveyItems.forEach(surveyItem => {
      dependencies.push(new LogicDependency(surveyItem));
    });

    this._dependencyCollection = dependencies.filter(
      dependency => dependency.isValid,
    );
  }

  _getApplicableDependencies(
    surveyItem: SurveyItem,
    ignoreLinks: string[] = [],
  ): Dependency[] {
    return this._dependencyCollection.filter(
      dependency =>
        surveyItem.link &&
        !ignoreLinks.includes(dependency.linkId) &&
        dependency.hasDependency(surveyItem),
    );
  }

  /**
   * Checks the dependency implications of removing the given survey item
   */
  checkRemoval(
    surveyItemToRemove: SurveyItem,
    ignoreLinks: string[] = [],
  ): DependencyCheckResult {
    const dependencies = this._getApplicableDependencies(
      surveyItemToRemove,
      ignoreLinks,
    );

    if (!dependencies.length) return notAffectedCheckResult;

    const individualResults = dependencies.map(dependency =>
      dependency.onRemoval(surveyItemToRemove),
    );

    return this._resultAggregator.getAggregateResult(individualResults);
  }

  /**
   * Checks the dependency implications of removing the given survey item
   */
  checkUpdate(
    originalSurveyItem: SurveyItem,
    updatedSurveyItem: SurveyItem,
  ): DependencyCheckResult {
    if (
      !originalSurveyItem ||
      !updatedSurveyItem ||
      originalSurveyItem.link !== updatedSurveyItem.link
    ) {
      console.error(
        'Tried to check survey item updates on invalid survey items',
      );
      return notAffectedCheckResult;
    }

    const dependencies = this._getApplicableDependencies(originalSurveyItem);

    if (!dependencies.length) return notAffectedCheckResult;

    const individualResults = dependencies.map(dependency =>
      dependency.onUpdate(originalSurveyItem, updatedSurveyItem),
    );

    return this._resultAggregator.getAggregateResult(individualResults);
  }
}
